import { useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { LifeBuoy, Send, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SEND_SUPPORT_MESSAGE } from "@/graphql/mutations/support";

/**
 * „Parašyk mums" — the support conversation, shared by the page and the modal.
 *
 * One component, two mountings, for the reason `TheoryView` and `ProgressReportView`
 * are: the page is the canonical destination (linkable, survives a reload, and
 * somewhere a 404 can send people) and the modal is the same thing without
 * losing what you were doing. Two implementations would drift, and this is the
 * screen somebody reaches when something is already wrong.
 *
 * **The conversation lives here, not in a table.** It is resent whole each turn.
 * A conversation nobody files is not worth storing, and one that is filed is
 * copied onto the row entire.
 *
 * **Two things are always true, and they are what make „the AI decides" safe:**
 * the reply says whether it filed (`filed` renders a card that cannot be missed),
 * and „Vis tiek registruoti" is always there. A model that declines to file
 * leaves somebody believing they reported something nobody has — that failure is
 * silent, and this button is how it stops being.
 */

/** The opening turn is fixed text, so opening the panel costs no wait and no AI
 *  call — the same call `DiscussionTutor::OPENING_INSTRUCTIONS` makes. */
const OPENING =
  "Parašyk, kas nutiko. Jei žinosiu atsakymą — pasakysiu iškart; jei ne — " +
  "perduosiu administratoriui ir pasakysiu, kad perdaviau.";

type Turn = { role: "user" | "assistant"; text: string };
type Filed = { id: string; categoryLabel: string; summary: string };

export function SupportChat({
  path,
  learnerId,
  onFiled,
  onSwitchToForm,
}: {
  /** Where they were. Attached to the request, never sent to the provider. */
  path?: string;
  learnerId?: string;
  onFiled?: (request: Filed) => void;
  /** Hand over to the form, carrying whatever they have said so far. */
  onSwitchToForm?: (text: string) => void;
}) {
  const [turns, setTurns] = useState<Turn[]>([{ role: "assistant", text: OPENING }]);
  const [draft, setDraft] = useState("");
  const [options, setOptions] = useState<string[]>([]);
  const [filed, setFiled] = useState<Filed | null>(null);
  // Whether the last turn came back with no model behind it. The form is the
  // route then, and it is offered by name rather than left to be discovered.
  const [degraded, setDegraded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [send, { loading: sending }] = useMutation(SEND_SUPPORT_MESSAGE);

  // Keep the newest turn in view. Same reason the tutor panel does it: the reply
  // is the whole point of pressing send.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [turns.length, filed]);

  const said = turns.some((t) => t.role === "user");
  // What to carry into the form: everything they typed, so switching costs them
  // nothing. Joined because a two-turn description is still one description.
  const lastSaid = turns.filter((t) => t.role === "user").map((t) => t.text).join("\n\n");

  async function submit(text?: string) {
    const value = (text ?? draft).trim();
    if (!value || sending) return;

    const next: Turn[] = [...turns, { role: "user", text: value }];
    setTurns(next);
    setDraft("");
    setOptions([]);

    try {
      const { data } = await send({
        variables: { messages: next.map((t) => ({ role: t.role, text: t.text })), path, learnerId },
      });
      const reply = (data as any)?.sendSupportMessage;
      if (!reply) return;

      setTurns([...next, { role: "assistant", text: reply.reply }]);
      setOptions(reply.options ?? []);
      setDegraded(!!reply.degraded);
      if (reply.request) {
        setFiled(reply.request);
        onFiled?.(reply.request);
      }
    } catch {
      setDegraded(true);
      // A failed turn must not eat what they typed: it is the only copy.
      setTurns([
        ...next,
        {
          role: "assistant",
          text:
            "Nepavyko išsiųsti. Pasirink temą apačioje ir paspausk „Registruoti“ — " +
            "žinutė vis tiek pasieks administratorių.",
        },
      ]);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        ref={scrollRef}
        className="max-h-[45vh] min-h-[8rem] overflow-y-auto rounded-lg border bg-muted/30 p-3"
      >
        <div className="flex flex-col gap-2.5">
          {turns.map((turn, i) => (
            <div
              key={i}
              className={
                turn.role === "user"
                  ? "self-end max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-3.5 py-2 text-sm text-primary-foreground"
                  : "self-start max-w-[90%] rounded-2xl rounded-bl-sm bg-background border px-3.5 py-2 text-sm"
              }
            >
              {turn.role === "assistant" && i === 0 && (
                <LifeBuoy className="mb-1 h-4 w-4 text-muted-foreground" />
              )}
              {turn.text}
            </div>
          ))}
          {sending && (
            <div className="self-start inline-flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Skaitau...
            </div>
          )}

          {/* The card that makes the model's decision visible. Not a toast: a
              toast is gone in four seconds, and „did my report go anywhere" is
              the question this whole screen exists to answer. */}
          {filed && (
            <div className="self-start w-full rounded-lg border border-green-300 bg-green-50 px-3.5 py-2.5 text-sm dark:border-green-800 dark:bg-green-950/40">
              <p className="flex items-center gap-1.5 font-medium text-green-900 dark:text-green-100">
                <CheckCircle2 className="h-4 w-4" />
                Užregistruota
              </p>
              <p className="mt-1 text-green-800 dark:text-green-200">
                {filed.categoryLabel} · Nr. {filed.id.slice(-6)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Tappable replies, only under the newest turn — the same rule the tutor
          follows, since tapping an answer to a question two turns ago would
          answer the wrong thing. */}
      {options.length > 0 && !sending && (
        <div className="flex flex-wrap gap-2">
          {options.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => submit(option)}
              className="rounded-full border bg-background px-3 py-1 text-sm hover:bg-accent cursor-pointer"
            >
              {option}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Kas nutiko?"
          rows={2}
          className="resize-none"
        />
        <Button onClick={() => submit()} disabled={!draft.trim() || sending} size="icon" aria-label="Siųsti">
          <Send className="h-4 w-4" />
        </Button>
      </div>

      {/* No select buried in the chat any more. With a real form mode there were
          two ways to file by hand, and this one asked somebody mid-conversation
          to classify their own problem in a dropdown. It hands them to the form
          instead, carrying what they already typed — see SupportPanel. */}
      {(said || degraded) && onSwitchToForm && (
        <button
          type="button"
          onClick={() => onSwitchToForm(lastSaid)}
          className="self-start text-sm text-primary hover:underline cursor-pointer"
        >
          {degraded ? "Užpildyti formą" : "Geriau užpildysiu formą"}
        </button>
      )}

    </div>
  );
}
